import useIntl from '@/utils/intl'
import {
  Close,
  DarkMode,
  KeyboardArrowDownOutlined,
  LightMode,
  Menu
} from '@mui/icons-material'
import {
  Badge,
  IconButton,
  InputBase,
  Modal,
  Slide,
  useScrollTrigger
} from '@mui/material'
import Image from 'next/image'
import { FunctionComponent, useState } from 'react'
import { useLanguage, Locale } from '@/context/LanguageContext'
import { useRouter } from 'next/navigation'
import AppBarMenus from './appBarMenus'
import AppBarMenuMobile from './appBarMenuMobile'
import DarkIconButton from '../ui/Buttons/SecondaryIconButton'
import { useContentContext } from '@/context/ContentContext'
import { IMG_URL, DOMAIN } from '@/utils/constants'
import classNames from 'classnames'
import { useCmsApi } from '@/services/cms-api'
import { GetSearchRes } from '@/services/cms-api/search/types'
import SearchResults from './searchResults'
import { checkLen, isEmptyString } from '@/utils/validations'
import { useTheme } from '@/context/ThemeContext'
import { useContextCart } from '@/context/Cart'

const supportedLanguages: Locale[] = ['mn', 'en']

interface Props {
  window?: () => Window
  children: React.ReactElement
}

const HideOnScroll: FunctionComponent<Props> = ({ children, window }) => {
  const trigger = useScrollTrigger({
    target: window != null ? window() : undefined
  })

  return (
    <div className='relative flex justify-center'>
      <Slide
        appear={false}
        direction='down'
        in={!trigger}
        className='rounded-2xl mt-5 fixed top-0 w-[95vw] z-50 bg-secondaryAdditional border border-surfaceInverse85 '
        mountOnEnter
        unmountOnExit
      >
        {children}
      </Slide>
    </div>
  )
}
const AppBar: FunctionComponent = () => {
  const { state } = useContextCart()
  const { translate } = useIntl()
  const router = useRouter()
  const [showMenu, setShowMenu] = useState(false)
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  // const [selectedTheme, setSelectedTheme] = useState('')
  const { locale, setLocale } = useLanguage()
  const { theme, setTheme } = useTheme()
  const { search } = useCmsApi()
  const [searchRes, setSearchRes] = useState<GetSearchRes>()
  const [showSearchRes, setShowSearchRes] = useState(false)
  const {
    state: { serviceCategories }
  } = useContentContext()
  const cartCount = state.items.reduce((sum, item) => sum + item.quantity, 0)

  // useEffect(() => {
  //   setSelectedTheme(localStorage.getItem('theme') ?? '')
  // }, [])

  const menus = [
    {
      text: translate('menu.service'),
      action: () => setShowMenu(!showMenu),
      id: 'menu.service'
    },
    {
      text: translate('menu.news'),
      action: () => router.push('/news'),
      id: 'menu.news'
    },
    {
      text: translate('menu.shop'),
      action: () => router.push('/shop'),
      id: 'menu.shop'
    },
    {
      text: '5G',
      action: () => window.open('https://support.mobicom.mn/5g', '_blank')
    }

  ]

  const handleSwitchLanguage = (): void => {
    const nextIdx = supportedLanguages.indexOf(locale) + 1
    if (nextIdx >= supportedLanguages.length) {
      setLocale(supportedLanguages[0])
    } else {
      setLocale(supportedLanguages[nextIdx])
    }
  }

  const searchFromWeb = async (value: string): Promise<void> => {
    try {
      if (!isEmptyString(value) && !checkLen(value, 1)) {
        const res = await search(value === undefined ? '' : value)
        if (res.length > 0) {
          setSearchRes(res)
          setShowSearchRes(true)
        } else {
          handleClear()
        }
      } else {
        handleClear()
      }
    } catch (e) {
      console.error(e)
    }
  }

  // const selectedLocale = languages[locale]

  const handleAction = (action: () => void): void => {
    action()
  }
  const handleClear = (): void => {
    setShowSearchRes(false)
    setSearchRes([])
  }

  const handleChangeMode = (): void => {
    if (theme === 'dark') {
      document.body.classList.remove('dark')
      setTheme('light')
    } else {
      document.body.classList.add('dark')
      setTheme('dark')
    }
  }

  const searchBox = (): any => (
    <InputBase
      className={classNames(
        'bg-appBar2 text-textAdditional rounded-full ',
        showMobileMenu ? 'flex mx-4' : 'hidden lg:flex ml-8'
      )}
      onChange={(e) => {
        searchFromWeb(e.target.value).catch((e) => console.error(e))
      }}
      sx={{ flex: 1 }}
      placeholder={translate('search')}
      startAdornment={
        <div className='flex flex-row justify-center items-center'>
          <IconButton className='text-textAdditional'>
            <img src={DOMAIN + '/icons/search.svg'} className='ml-2' />
          </IconButton>
        </div>
      }
    />
  )
  return (
    <HideOnScroll>
      <div className='w-full'>
        <div className='flex lg:items-center items-start justify-between p-3 lg:py-4 lg:px-6'>
          <div className='flex lg:w-1/3'>
            <Image
              draggable={false}
              width={70}
              height={34}
              className='cursor-pointer w-16 object-contain'
              onClick={() => router.push('/')}
              src={IMG_URL + '/uploads/mobicom-logo-hq.png'}
              alt='logo'
            />
            <div className='lg:w-70'>
              {searchBox()}
              {searchRes !== undefined && searchRes?.length > 0
                ? (
                  <Modal
                    open={showSearchRes}
                    onClose={() => setShowSearchRes(false)}
                    className='hidden lg:flex'
                  >
                    <SearchResults
                      results={searchRes}
                      handleClose={() => setShowSearchRes(false)}
                    />
                  </Modal>
                  )
                : (
                  <></>
                  )}
            </div>
          </div>
          <ul className='text-textAdditional font-medium text-sm hidden lg:flex items-center justify-center lg:w-1/3'>
            {menus.map((item, i) => (
              <li
                id={item.id}
                key={i}
                onClick={() => handleAction(item.action)}
                className='px-3 xl:px-4 cursor-pointer hover:bg-appBar2 rounded-lg p-2 font-semibold'
              >
                {item.text}
                {i === 0 ? <KeyboardArrowDownOutlined /> : ''}
              </li>
            ))}
            <Modal
              open={showMenu}
              onClose={() => setShowMenu(false)}
              className='hidden lg:flex'
            >
              <AppBarMenus
                categories={serviceCategories ?? []}
                handleClose={() => setShowMenu(false)}
              />
            </Modal>
            {/* )
              : <></>
          } */}
          </ul>
          <div className='flex gap-2 lg:hidden'>
            {showMobileMenu ? (
              <DarkIconButton
                type='onDark'
                iconImg={IMG_URL + '/uploads/icons/language.svg'}
                size='sm'
                onClick={handleSwitchLanguage}
              />
            ) : (
              <div className='flex gap-2'>
                <DarkIconButton
                  type='onDark'
                  icon={
                    theme === 'dark'
                      ? (
                        <DarkMode className='text-textAdditional' />
                        )
                      : (
                        <LightMode />
                        )
                  }
                  size='sm'
                  onClick={handleChangeMode}
                />
                <div className='relative'>
                  <Badge
                    badgeContent={cartCount}
                    color='error'
                    invisible={cartCount <= 0}
                    sx={{
                      '& .MuiBadge-badge': {
                        width: '16px',
                        height: '16px',
                        fontSize: '10px', // Smaller font to fit the badge size
                        minWidth: '16px',
                        lineHeight: '16px',
                        top: '6px',
                        right: '6px',
                        backgroundColor: 'primary',
                        borderRadius: '50%',
                        border: '1px solid rgba(var(--secondaryAdditional))'
                      }
                    }}
                  >
                    <div
                      className='relative'
                      style={{
                        padding: '1px',
                        backgroundColor: 'transparent',
                        borderRadius: '50%'
                      }}
                    >
                      <DarkIconButton
                        type='onDark'
                        iconImg={IMG_URL + '/uploads/icons/shoppingbag.svg'}
                        size='sm'
                        onClick={() => router.push('/cart')}
                      />
                    </div>
                  </Badge>
                </div>
              </div>
            )}
            <DarkIconButton
              type='onDark'
              icon={
                showMobileMenu
                  ? (
                    <Close className='text-textAdditional' />
                    )
                  : (
                    <Menu className='text-textAdditional' />
                    )
              }
              size='sm'
              onClick={() => setShowMobileMenu(!showMobileMenu)}
            />
          </div>
          <div className='hidden lg:flex justify-end w-1/3 gap-2'>
            <DarkIconButton
              type='onDark'
              icon={
                theme === 'dark'
                  ? (
                    <DarkMode className='text-textAdditional' />
                    )
                  : (
                    <LightMode />
                    )
              }
              size='sm'
              onClick={handleChangeMode}
            />
            <DarkIconButton
              type='onDark'
              iconImg={IMG_URL + '/uploads/icons/language.svg'}
              size='sm'
              onClick={handleSwitchLanguage}
            />
            {/* <DarkIconButton type='onDark' iconImg={IMG_URL + '/uploads/icons/shoppingbag.svg'} size='sm' onClick={() => router.push('/cart')} /> */}
            <div className='relative'>
              <Badge
                badgeContent={cartCount}
                color='error'
                invisible={cartCount <= 0}
                sx={{
                  '& .MuiBadge-badge': {
                    width: '16px',
                    height: '16px',
                    fontSize: '10px', // Smaller font to fit the badge size
                    minWidth: '16px',
                    lineHeight: '16px',
                    top: '6px',
                    right: '6px',
                    backgroundColor: 'primary',
                    borderRadius: '50%',
                    border: '1px solid rgba(var(--secondaryAdditional))'
                  }
                }}
              >
                <div
                  className='relative'
                  style={{
                    padding: '1px',
                    backgroundColor: 'transparent',
                    borderRadius: '50%'
                  }}
                >
                  <DarkIconButton
                    type='onDark'
                    iconImg={IMG_URL + '/uploads/icons/shoppingbag.svg'}
                    size='sm'
                    onClick={() => router.push('/cart')}
                  />
                </div>
              </Badge>
            </div>
          </div>
          {/* <ContainedButton title={translate('login')} /> */}
        </div>
        {showMobileMenu
          ? (
            <AppBarMenuMobile
              results={searchRes !== undefined ? searchRes : []}
              onClose={() => setShowMobileMenu(false)}
              categories={serviceCategories ?? []}
            />
            )
          : (
            <></>
            )}
        {/* <Modal
        open={showMobileMenu}
        onClose={() => setShowMenu(false)}
        className='lg:hidden'
      >
        <AppBarMenuMobile />
      </Modal> */}
      </div>
    </HideOnScroll>
  )
}
export default AppBar
